import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ClipboardList, Search, MousePointerClick, BadgeCheck, Wallet, ShieldCheck, FileText, Users, Headphones } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { api, inr } from "../../lib/api";
import { PlanCard } from "../../components/PlanCard";

export function usePublic() {
  const [plans, setPlans] = useState([]);
  const [info, setInfo] = useState(null);
  useEffect(() => {
    api.get("/public/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/public/info").then((r) => setInfo(r.data)).catch(() => {});
  }, []);
  return { plans, info };
}

export const Section = ({ eyebrow, title, sub, children, className = "", testid }) => (
  <section className={`relative mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8 ${className}`} data-testid={testid}>
    {eyebrow && <p className="text-xs font-black uppercase tracking-[0.2em] text-[#D4AF37]">{eyebrow}</p>}
    {title && <h2 className="mt-3 max-w-3xl text-3xl font-black tracking-tight text-white sm:text-4xl lg:text-5xl">{title}</h2>}
    {sub && <p className="mt-4 max-w-2xl text-base font-medium text-slate-400 md:text-lg">{sub}</p>}
    <div className="mt-12">{children}</div>
  </section>
);

export function Steps() {
  const { t } = useLang();
  const steps = [
    [Search, "Choose an available survey you're eligible for", "अपनी पात्रता के अनुसार उपलब्ध सर्वे चुनें"],
    [ClipboardList, "Every survey has exactly 5 questions", "हर सर्वे में ठीक 5 प्रश्न होते हैं"],
    [MousePointerClick, "Answer honestly and submit", "सही तरीके से सर्वे पूरा करें"],
    [BadgeCheck, "After verification, eligible rewards are credited", "सत्यापन के बाद पात्र रिवॉर्ड क्रेडिट होता है"],
  ];
  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {steps.map(([Icon, en, hi], i) => (
        <div key={i} className="glass group rounded-[2rem] p-8 transition-all hover:-translate-y-2 hover:bg-white/[0.04]" style={{ animationDelay: `${i * 100}ms` }} data-testid={`step-${i + 1}`}>
          <div className="flex items-center justify-between">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-400 transition-colors group-hover:bg-blue-500/20"><Icon size={28} /></div>
            <span className="text-4xl font-black text-white/5 transition-colors group-hover:text-white/10">0{i + 1}</span>
          </div>
          <p className="mt-6 text-lg font-bold leading-snug text-slate-200">{t(en, hi)}</p>
        </div>
      ))}
    </div>
  );
}

function HeroVisual() {
  const { t } = useLang();
  const APP_3D = "https://static.prod-images.emergentagent.com/jobs/38be2cca-7a2d-47ee-b124-f51241ae0f7f/images/54661f4d417e538720743702daa56637c42b3bef7ffe9926d957a3945990888a.jpeg";
  return (
    <div className="relative mx-auto w-full max-w-md">
      <div className="absolute -inset-10 rounded-[4rem] dotgrid opacity-30" />
      <div className="absolute -inset-20 rounded-full bg-blue-500/20 blur-[100px]" />
      <div className="absolute -inset-20 translate-x-20 translate-y-20 rounded-full bg-[#D4AF37]/10 blur-[100px]" />
      
      <div className="relative float-slow overflow-hidden rounded-[2.5rem] border border-white/10 shadow-2xl">
        <img src={APP_3D} alt="SurveyKaro App" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#05070A]/80 via-transparent to-transparent" />
      </div>

      <div className="absolute -left-8 top-1/4 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 shadow-xl backdrop-blur-xl">
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{t("Registration", "रजिस्ट्रेशन")}</p>
        <p className="mt-1 text-lg font-black text-emerald-400">{t("Always free", "हमेशा मुफ्त")}</p>
      </div>
      <div className="absolute -bottom-6 -right-6 rounded-2xl border border-[#D4AF37]/20 bg-[#D4AF37]/10 px-5 py-4 shadow-xl backdrop-blur-xl">
        <p className="text-[10px] font-black uppercase tracking-widest text-[#D4AF37]">{t("Rewards", "रिवॉर्ड")}</p>
        <p className="mt-1 text-lg font-black text-white">{t("Verified", "सत्यापित")}</p>
      </div>
    </div>
  );
}

export default function Home() {
  const { t } = useLang();
  const { plans, info } = usePublic();
  return (
    <div className="relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-1/2 h-[800px] w-full -translate-x-1/2 bg-[radial-gradient(ellipse_at_top,rgba(59,130,246,0.15),transparent_70%)]" />
      <div className="absolute top-40 right-0 h-[600px] w-[600px] bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.08),transparent_70%)]" />

      <section className="relative pt-16 md:pt-24" data-testid="hero-section">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-4 pb-20 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:px-8">
          <div className="fade-up relative z-10">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-4 py-1.5 text-xs font-bold tracking-widest uppercase text-emerald-400">
              <CheckCircle2 size={16} /> {t("Free registration • No fee to create an account", "मुफ्त रजिस्ट्रेशन • अकाउंट बनाने का कोई शुल्क नहीं")}
            </span>
            <h1 className="mt-8 text-5xl font-black leading-[1.1] tracking-tight text-white sm:text-6xl lg:text-7xl" data-testid="hero-title">
              {t("Complete Surveys", "सर्वे करें")} <br />
              <span className="text-gradient-gold">{t("Earn Rewards", "रिवॉर्ड कमाएँ")}</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg font-medium leading-relaxed text-slate-400">
              {t("SurveyKaro lets eligible participants complete research surveys and receive rewards for approved participation.", "SurveyKaro पात्र प्रतिभागियों को शोध सर्वे पूरा करने और स्वीकृत भागीदारी के लिए रिवॉर्ड पाने का अवसर देता है।")}
            </p>
            <p className="mt-3 text-sm font-bold uppercase tracking-widest text-slate-500">आपकी राय से बेहतर समाज और बेहतर अवसर।</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/register" className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#B8860B] px-8 py-4 text-sm font-black uppercase tracking-wider text-[#05070A] shadow-[0_0_30px_rgba(212,175,55,0.3)] transition-all hover:scale-[1.02] hover:shadow-[0_0_40px_rgba(212,175,55,0.5)]" data-testid="hero-register-btn">
                {t("Create free account", "मुफ्त अकाउंट बनाएँ")} <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/how-it-works" className="rounded-full border border-white/10 bg-white/5 px-8 py-4 text-sm font-black uppercase tracking-wider text-white backdrop-blur-md transition-colors hover:bg-white/10" data-testid="hero-how-btn">
                {t("How it works", "कैसे काम करता है")}
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap gap-x-10 gap-y-4 text-sm font-medium text-slate-500">
              <span data-testid="hero-live-surveys"><b className="text-white text-lg">{info ? info.stats.active_surveys : "—"}</b><br/>{t("Live funded surveys right now", "अभी सक्रिय फंडेड सर्वे")}</span>
              <span><b className="text-white text-lg">5</b><br/>{t("Questions per survey", "हर सर्वे में प्रश्न")}</span>
            </div>
          </div>
          <HeroVisual />
        </div>
      </section>

      <Section eyebrow={t("How it works", "कैसे काम करता है")} title={t("Four simple steps", "चार आसान कदम")} testid="home-steps">
        <Steps />
      </Section>

      <section className="relative border-y border-white/5 bg-[#0A0D14]/50 py-10" data-testid="home-plans">
        <Section eyebrow={t("Plans", "प्लान")} title={t("Choose a survey access plan", "सर्वे एक्सेस प्लान चुनें")}
          sub={t("Registration is free. Paid plans unlock access to specific survey categories. Daily caps are maximums, not guaranteed earnings.", "रजिस्ट्रेशन मुफ्त है। पेड प्लान विशेष सर्वे श्रेणियों तक पहुँच देते हैं। दैनिक सीमाएँ अधिकतम हैं, गारंटीशुदा कमाई नहीं।")}>
          <div className="grid gap-8 md:grid-cols-3">{plans.map((p) => <PlanCard key={p.id} plan={p} />)}</div>
          <div className="mt-12 flex justify-center"><Link to="/plans" className="rounded-full border border-white/10 bg-white/5 px-8 py-4 text-sm font-black uppercase tracking-wider text-white transition-colors hover:bg-white/10" data-testid="home-compare-plans">{t("Compare plans & Pay Now", "प्लान तुलना करें और अभी भुगतान करें")}</Link></div>
        </Section>
      </section>

      <Section eyebrow={t("Transparency", "पारदर्शिता")} title={t("Built around clear records", "स्पष्ट रिकॉर्ड पर आधारित")} testid="home-transparency">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[
            [FileText, "Clear plans and fees", "स्पष्ट प्लान और फीस", "Exact amount incl. GST shown before checkout.", "चेकआउट से पहले GST सहित सटीक राशि।"],
            [ClipboardList, "Survey reward records", "सर्वे रिवॉर्ड का रिकॉर्ड", "Pending, approved and rejected — each with a reason.", "लंबित, स्वीकृत, अस्वीकृत — हर एक कारण सहित।"],
            [Users, "Separate survey & referral accounts", "अलग सर्वे और रेफरल हिसाब", "Never merged. Referral programme currently disabled.", "कभी मिलाया नहीं जाता। रेफरल प्रोग्राम अभी बंद है।"],
            [Wallet, "Withdrawal breakdown", "निकासी का पूरा हिसाब", "Gross, TDS, fees and net payout before you request.", "अनुरोध से पहले कुल, TDS, शुल्क और नेट भुगतान।"],
            [ShieldCheck, "Secure & private", "सुरक्षित और निजी", "Encrypted KYC, masked bank details, audit logs.", "एन्क्रिप्टेड KYC, छिपे बैंक विवरण, ऑडिट लॉग।"],
            [Headphones, "Support & grievance", "सहायता और शिकायत", "Ticket-based support with full history.", "पूरी हिस्ट्री के साथ टिकट सहायता।"],
          ].map(([Icon, en, hi, den, dhi], i) => (
            <div key={i} className="glass rounded-[2rem] p-8 transition-all hover:-translate-y-2">
              <div className="inline-flex rounded-2xl bg-blue-500/10 p-4 text-blue-400"><Icon size={24} /></div>
              <p className="mt-6 text-lg font-bold text-white">{t(en, hi)}</p>
              <p className="mt-2 text-sm font-medium text-slate-400 leading-relaxed">{t(den, dhi)}</p>
            </div>
          ))}
        </div>
      </Section>

      <section className="relative mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <div className="glass-gold relative flex flex-col items-start justify-between gap-8 overflow-hidden rounded-[3rem] p-10 md:flex-row md:items-center md:p-16">
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[#D4AF37]/20 blur-[80px]" />
          <div className="relative z-10">
            <h2 className="text-3xl font-black tracking-tight text-white md:text-4xl">{t("Create your free account today", "आज ही अपना मुफ्त अकाउंट बनाएँ")}</h2>
            <p className="mt-3 text-sm font-medium text-slate-400">{t("Withdrawal minimum", "न्यूनतम निकासी")}: <b className="text-white">{info ? inr(info.withdrawals.min_amount) : "—"}</b> • {info?.withdrawals?.schedule_note}</p>
          </div>
          <Link to="/register" className="relative z-10 shrink-0 rounded-full bg-white px-8 py-4 text-sm font-black uppercase tracking-wider text-[#05070A] shadow-xl transition-all hover:scale-[1.02]" data-testid="cta-register-btn">{t("Join free", "मुफ्त जुड़ें")}</Link>
        </div>
      </section>
    </div>
  );
}
